import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import '@/App.css';
import Header from '@/components/layout/Header';
import SpanishHeader from '@/components/layout/SpanishHeader';
import AnnouncementBanner from '@/components/common/AnnouncementBanner';
import Footer from '@/components/layout/Footer';
import SpanishFooter from '@/components/layout/SpanishFooter';
import RouteSchemaInjector from '@/components/seo/RouteSchemaInjector';
import RouteRobotsManager from '@/components/seo/RouteRobotsManager';
import LangAlternatesManager from '@/components/seo/LangAlternatesManager';
import RedirectManager from '@/components/seo/RedirectManager';
import CanonicalManager from '@/components/seo/CanonicalManager';

// Redirect www to non-www for SEO (canonical URL enforcement)
if (typeof window !== 'undefined' && window.location.hostname.startsWith('www.')) {
  window.location.href = window.location.href.replace('www.', '');
}

// ScrollToTop component - scrolls to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pathname]);
  
  return null;
};

// Redirect /blog/:slug to /blogs/:slug
const BlogPostRedirect = () => {
  const { slug } = useParams();
  return <Navigate to={`/blogs/${slug}`} replace />;
};

// Pages
import Home from '@/pages/Home';
import AboutUs from '@/pages/AboutUs';
import OurTeam from '@/pages/OurTeam';
import OurOffices from '@/pages/OurOffices';
import DentalImplants from '@/pages/services/DentalImplants';
import Invisalign from '@/pages/services/Invisalign';
import CosmeticDentistry from '@/pages/services/CosmeticDentistry';
import LANAP from '@/pages/services/LANAP';
import EmergencyDentist from '@/pages/services/EmergencyDentist';
import BoneGrafting from '@/pages/services/BoneGrafting';
import Bruxism from '@/pages/services/Bruxism';
import CompositeFillings from '@/pages/services/CompositeFillings';
import CrownsAndBridges from '@/pages/services/CrownsAndBridges';
import Dentures from '@/pages/services/Dentures';
import Gingivectomy from '@/pages/services/Gingivectomy';
import GuidedTissueBone from '@/pages/services/GuidedTissueBone';
import GumDisease from '@/pages/services/GumDisease';
import LaserDentistry from '@/pages/services/LaserDentistry';
import OcclusalAdjustment from '@/pages/services/OcclusalAdjustment';
import OralCancerScreening from '@/pages/services/OralCancerScreening';
import OsseousSurgery from '@/pages/services/OsseousSurgery';
import PorcelainVeneers from '@/pages/services/PorcelainVeneers';
import FirstFitVeneers from '@/pages/services/FirstFitVeneers';
import PreventativeDentistry from '@/pages/services/PreventativeDentistry';
import RidgeAugmentation from '@/pages/services/RidgeAugmentation';
import RidgePreservation from '@/pages/services/RidgePreservation';
import RootCanals from '@/pages/services/RootCanals';
import ScalingRootPlaning from '@/pages/services/ScalingRootPlaning';
import Sedation from '@/pages/services/Sedation';
import TeethWhitening from '@/pages/services/TeethWhitening';
import Specials from '@/pages/specials/Specials';
import DentalImplantsSpecial from '@/pages/specials/DentalImplantsSpecial';
import InvisalignSpecial from '@/pages/specials/InvisalignSpecial';
import CleaningSpecial from '@/pages/specials/CleaningSpecial';
import EmergencySpecial from '@/pages/specials/EmergencySpecial';
import LANAPSpecial from '@/pages/specials/LANAPSpecial';
import Financing from '@/pages/Financing';
import Testimonials from '@/pages/Testimonials';
import Contact from '@/pages/Contact';

// Implant Center Pages
import ImplantCenterHome from '@/pages/implant-center/ImplantCenterHome';
import DentalImplantsPage from '@/pages/implant-center/DentalImplantsPage';
import MiniDentalImplants from '@/pages/implant-center/MiniDentalImplants';
import JawboneGrafting from '@/pages/implant-center/JawboneGrafting';
import ImplantCosmeticDentistry from '@/pages/implant-center/ImplantCosmeticDentistry';
import DentalPlan from '@/pages/implant-center/DentalPlan';

// SEO Pages
import Blog from '@/pages/seo/Blog';
import BlogPost from '@/pages/seo/BlogPost';
import BlogArchive from '@/pages/seo/BlogArchive';
import DentalImplantCost from '@/pages/seo/DentalImplantCost';
import AffordableDentalImplants from '@/pages/seo/AffordableDentalImplants';
import InvisalignCost from '@/pages/seo/InvisalignCost';

// Location Pages
import DentistNearBenbrook from '@/pages/locations/DentistNearBenbrook';
import DentistNearAledo from '@/pages/locations/DentistNearAledo';
import DentistNearWestoverHills from '@/pages/locations/DentistNearWestoverHills';
import AreasWeServe from '@/pages/AreasWeServe';

// SEO Landing Pages
import TopDentistsFortWorth from '@/pages/TopDentistsFortWorth';
import DentalImplantsFortWorth from '@/pages/DentalImplantsFortWorth';
import DentistNearMe from '@/pages/DentistNearMe';
import BestDentistFortWorth from '@/pages/BestDentistFortWorth';
import WhyChooseFortWorthDental from '@/pages/WhyChooseFortWorthDental';

// Client Payment Portal
import FortWorthDentalPayment from '@/pages/FortWorthDentalPayment';
import BestDentistNearMe from '@/pages/BestDentistNearMe';

// Legal Pages
import Terms from '@/pages/Terms';
import Privacy from '@/pages/Privacy';
import HipaaNPP from '@/pages/HipaaNPP';
import SitemapPage from '@/pages/SitemapPage';

// Admin Pages
import AdminLogin from '@/pages/admin/AdminLogin';
import AdminLayout from '@/pages/admin/AdminLayout';
import AdminDashboard from '@/pages/admin/AdminDashboard';
import ManageSpecials from '@/pages/admin/ManageSpecials';
import ManageHomepage from '@/pages/admin/ManageHomepage';
import ManageABTests from '@/pages/admin/ManageABTests';
import AnalyticsDashboard from '@/pages/admin/AnalyticsDashboard';
import PressReleasesAdmin from '@/pages/admin/PressReleases';
import ManageSyndication from '@/pages/admin/ManageSyndication';
import ManageChatLogs from '@/pages/admin/ManageChatLogs';
import ManageBlog from '@/pages/admin/ManageBlog';
import GBPAutomation from '@/pages/admin/GBPAutomation';
import GBPQAMonitor from '@/pages/admin/GBPQAMonitor';
import CitationBuilder from '@/pages/admin/CitationBuilder';
import VotingReminder from '@/pages/admin/VotingReminder';
import VotingUnsubscribe from '@/pages/VotingUnsubscribe';
import SEOIntelligence from '@/pages/admin/SEOIntelligence';
import ServiceAreas from '@/pages/admin/ServiceAreas';
import ReviewGeneration from '@/pages/admin/ReviewGeneration';
import ReviewResponses from '@/pages/admin/ReviewResponses';
import LinkBuilding from '@/pages/admin/LinkBuilding';
import CompetitorMonitoring from '@/pages/admin/CompetitorMonitoring';
import LocalSchema from '@/pages/admin/LocalSchema';
import InternalLinking from '@/pages/admin/InternalLinking';
import IndexNowAdmin from '@/pages/admin/IndexNowAdmin';
import AdminIndexing from '@/pages/admin/AdminIndexing';
import ROIDashboard from '@/pages/admin/ROIDashboard';
import DataSync from '@/pages/admin/DataSync';
import BilingualTranslation from '@/pages/admin/BilingualTranslation';
import AdminCRM from '@/pages/admin/AdminCRM';
import AdminUsers from '@/pages/admin/AdminUsers';
import SocialWebhooks from '@/pages/admin/SocialWebhooks';
import AdminRedirectManager from '@/pages/admin/RedirectManager';
import GA4Analytics from '@/pages/admin/GA4Analytics';
import NotFound from '@/pages/NotFound';

// Dynamic Service Area Pages
import ServiceAreaPage from '@/pages/ServiceAreaPage';

// Spanish (ES) parallel router — Phase 1 of bilingual conversion
import EsRouter from '@/pages/es/EsRouter';

// Common Components
import ChatWidget from '@/components/common/ChatWidget';
import AppointmentModal from '@/components/common/AppointmentModal';
import FloatingAppointmentButton from '@/components/common/FloatingAppointmentButton';

// Public Pages
import PressReleasesPage from '@/pages/PressReleases';

// Layout component that conditionally renders Header/Footer
const Layout = ({ children }) => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');
  const isEsRoute = location.pathname === '/es' || location.pathname.startsWith('/es/');
  const [isAppointmentModalOpen, setIsAppointmentModalOpen] = React.useState(false);

  const handleOpenAppointmentModal = () => {
    setIsAppointmentModalOpen(true);
  };

  const handleCloseAppointmentModal = () => {
    setIsAppointmentModalOpen(false);
  };
  
  if (isAdminRoute) {
    return <>{children}</>;
  }
  
  return (
    <>
      <AnnouncementBanner />
      {isEsRoute
        ? <SpanishHeader onBookAppointment={handleOpenAppointmentModal} />
        : <Header onBookAppointment={handleOpenAppointmentModal} />}
      <main>{children}</main>
      {isEsRoute ? <SpanishFooter /> : <Footer />}
      <ChatWidget />
      <FloatingAppointmentButton onClick={handleOpenAppointmentModal} />
      <AppointmentModal 
        isOpen={isAppointmentModalOpen} 
        onClose={handleCloseAppointmentModal} 
      />
    </>
  );
};

function App() {
  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
          <ScrollToTop />
          <RedirectManager />
          <CanonicalManager />
          <RouteRobotsManager />
          <LangAlternatesManager />
          <RouteSchemaInjector />
          <Layout>
            <Routes>
              {/* Admin Routes - No Header/Footer */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={<AdminLayout />}>
                <Route path="dashboard" element={<AdminDashboard />} />
                <Route path="specials" element={<ManageSpecials />} />
                <Route path="homepage" element={<ManageHomepage />} />
                <Route path="ab-tests" element={<ManageABTests />} />
                <Route path="analytics" element={<AnalyticsDashboard />} />
                <Route path="press-releases" element={<PressReleasesAdmin />} />
                <Route path="syndication" element={<ManageSyndication />} />
                <Route path="chat-logs" element={<ManageChatLogs />} />
                <Route path="blog" element={<ManageBlog />} />
                <Route path="gbp" element={<GBPAutomation />} />
                <Route path="gbp-qa" element={<GBPQAMonitor />} />
                <Route path="citation-builder" element={<CitationBuilder />} />
                <Route path="voting-reminder" element={<VotingReminder />} />
                <Route path="seo-intelligence" element={<SEOIntelligence />} />
                <Route path="service-areas" element={<ServiceAreas />} />
                <Route path="reviews" element={<ReviewGeneration />} />
                <Route path="review-responses" element={<ReviewResponses />} />
                <Route path="google-auth-callback" element={<ReviewResponses />} />
                <Route path="link-building" element={<LinkBuilding />} />
                <Route path="competitors" element={<CompetitorMonitoring />} />
                <Route path="local-schema" element={<LocalSchema />} />
                <Route path="internal-linking" element={<InternalLinking />} />
                <Route path="indexnow" element={<IndexNowAdmin />} />
                <Route path="google-indexing" element={<AdminIndexing />} />
                <Route path="roi-dashboard" element={<ROIDashboard />} />
                <Route path="data-sync" element={<DataSync />} />
                <Route path="bilingual" element={<BilingualTranslation />} />
                <Route path="crm" element={<AdminCRM />} />
                <Route path="users" element={<AdminUsers />} />
                <Route path="social-webhooks" element={<SocialWebhooks />} />
                <Route path="redirects" element={<AdminRedirectManager />} />
                <Route path="ga4-analytics" element={<GA4Analytics />} />
              </Route>

              {/* Main Pages - Matching Original WordPress URLs */}
              <Route path="/" element={<Home />} />
              <Route path="/about-us" element={<AboutUs />} />
              <Route path="/our-team" element={<OurTeam />} />
              <Route path="/our-offices" element={<OurOffices />} />
              <Route path="/contact-us" element={<Contact />} />
              <Route path="/testimonials" element={<Testimonials />} />
              <Route path="/form-finances" element={<Financing />} />
              
              {/* Service Pages - Root Level URLs (Matching WordPress) */}
              <Route path="/dental-implants" element={<DentalImplants />} />
              <Route path="/invisalign" element={<Invisalign />} />
              <Route path="/lanap" element={<LANAP />} />
              <Route path="/bone-grafting" element={<BoneGrafting />} />
              <Route path="/bruxism-teeth-grinding" element={<Bruxism />} />
              <Route path="/composite-fillings" element={<CompositeFillings />} />
              <Route path="/crowns-bridges" element={<CrownsAndBridges />} />
              <Route path="/dentures" element={<Dentures />} />
              <Route path="/gingivectomy" element={<Gingivectomy />} />
              <Route path="/guided-tissue-bone-regeneration" element={<GuidedTissueBone />} />
              <Route path="/gum-disease" element={<GumDisease />} />
              <Route path="/laser-dentistry" element={<LaserDentistry />} />
              <Route path="/occlusal-bite-adjustment" element={<OcclusalAdjustment />} />
              <Route path="/oral-cancer-screening" element={<OralCancerScreening />} />
              <Route path="/osseous-surgery" element={<OsseousSurgery />} />
              <Route path="/porcelain-veneers" element={<PorcelainVeneers />} />
              <Route path="/first-fit-veneers" element={<FirstFitVeneers />} />
              <Route path="/preventative-dentistry" element={<PreventativeDentistry />} />
              <Route path="/ridge-augmentation" element={<RidgeAugmentation />} />
              <Route path="/ridge-preservation" element={<RidgePreservation />} />
              <Route path="/root-canals" element={<RootCanals />} />
              <Route path="/scaling-root-planing" element={<ScalingRootPlaning />} />
              <Route path="/sedation" element={<Sedation />} />
              <Route path="/teeth-whitening" element={<TeethWhitening />} />
              <Route path="/cosmetic-dentistry" element={<CosmeticDentistry />} />
              <Route path="/emergency-dentist" element={<EmergencyDentist />} />
              
              {/* Specials Pages - Matching WordPress URLs */}
              <Route path="/specials" element={<Specials />} />
              <Route path="/dental-implants-free-consult-with-x-rays" element={<DentalImplantsSpecial />} />
              <Route path="/invisalign-free-consultation" element={<InvisalignSpecial />} />
              <Route path="/179-healthy-cleaning-exam-and-set-of-x-rays" element={<CleaningSpecial />} />
              <Route path="/99-emergency-exam-and-x-ray-affordable-dental-services" element={<EmergencySpecial />} />
              <Route path="/free-lanap-consultation" element={<LANAPSpecial />} />
              
              {/* Implant Center (Subdomain Content) */}
              <Route path="/implant-center" element={<ImplantCenterHome />} />
              <Route path="/implant-center/dental-implants" element={<DentalImplantsPage />} />
              <Route path="/implant-center/mini-dental-implants" element={<MiniDentalImplants />} />
              <Route path="/implant-center/jawbone-grafting-and-sinus-lift" element={<JawboneGrafting />} />
              <Route path="/implant-center/cosmetic-dentistry-at-fort-worth-dental" element={<ImplantCosmeticDentistry />} />
              <Route path="/implant-center/fort-worth-dental-provides-a-dental-plan-you-can-afford" element={<DentalPlan />} />
              
              {/* Press Releases */}
              <Route path="/press-releases" element={<PressReleasesPage />} />
              <Route path="/press-releases/:releaseId" element={<PressReleasesPage />} />
              
              {/* SEO Blog Routes - Matching WordPress */}
              <Route path="/blogs" element={<Blog />} />
              <Route path="/blog" element={<Navigate to="/blogs" replace />} />
              <Route path="/blog/archive" element={<BlogArchive />} />
              <Route path="/blogs/archive" element={<BlogArchive />} />
              <Route path="/blog/:slug" element={<BlogPostRedirect />} />
              <Route path="/blogs/:slug" element={<BlogPost />} />
              
              {/* SEO Cost/Pricing Pages */}
              <Route path="/dental-implant-cost-fort-worth" element={<DentalImplantCost />} />
              <Route path="/affordable-dental-implants-fort-worth" element={<AffordableDentalImplants />} />
              <Route path="/invisalign-cost-fort-worth" element={<InvisalignCost />} />
              
              {/* Location/Near Me Pages */}
              <Route path="/dentist-near-benbrook" element={<DentistNearBenbrook />} />
              <Route path="/dentist-near-aledo" element={<DentistNearAledo />} />
              <Route path="/dentist-near-westover-hills" element={<DentistNearWestoverHills />} />
              <Route path="/areas-we-serve" element={<AreasWeServe />} />
              
              {/* SEO Landing Pages */}
              <Route path="/top-dentists-fort-worth" element={<TopDentistsFortWorth />} />
              <Route path="/dental-implants-fort-worth" element={<DentalImplantsFortWorth />} />
              <Route path="/dentist-near-me" element={<DentistNearMe />} />
              <Route path="/best-dentist-fort-worth" element={<BestDentistFortWorth />} />
              <Route path="/best-dentist-near-me" element={<BestDentistNearMe />} />
              <Route path="/why-choose-fort-worth-dental" element={<WhyChooseFortWorthDental />} />
              
              {/* Client Payment Portal */}
              <Route path="/fortworthdental" element={<FortWorthDentalPayment />} />

              {/* Spanish (ES) parallel router — Phase 1 of bilingual conversion.
                  MUST come before the /:slug catch-all below, otherwise "/es" is
                  treated as a single-segment service-area slug. The /es/* pattern
                  matches both /es and /es/anything via the index route inside
                  EsRouter. */}
              <Route path="/es/*" element={<EsRouter />} />

              {/* Dynamic Service Area Pages */}
              <Route path="/:slug" element={<ServiceAreaPage />} />
              
              {/* Legal Pages */}
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/hipaa-npp" element={<HipaaNPP />} />
              <Route path="/sitemap" element={<SitemapPage />} />
              
              {/* 301 Redirects for Old URLs (if someone has old bookmarks) */}
              <Route path="/contact" element={<Navigate to="/contact-us" replace />} />
              <Route path="/financing" element={<Navigate to="/form-finances" replace />} />
              <Route path="/services/bone-grafting" element={<Navigate to="/bone-grafting" replace />} />
              <Route path="/services/bruxism" element={<Navigate to="/bruxism-teeth-grinding" replace />} />
              <Route path="/services/composite-fillings" element={<Navigate to="/composite-fillings" replace />} />
              <Route path="/services/crowns-bridges" element={<Navigate to="/crowns-bridges" replace />} />
              <Route path="/services/dentures" element={<Navigate to="/dentures" replace />} />
              <Route path="/services/gingivectomy" element={<Navigate to="/gingivectomy" replace />} />
              <Route path="/services/guided-tissue-bone" element={<Navigate to="/guided-tissue-bone-regeneration" replace />} />
              <Route path="/services/gum-disease" element={<Navigate to="/gum-disease" replace />} />
              <Route path="/services/laser-dentistry" element={<Navigate to="/laser-dentistry" replace />} />
              <Route path="/services/occlusal-adjustment" element={<Navigate to="/occlusal-bite-adjustment" replace />} />
              <Route path="/services/oral-cancer-screening" element={<Navigate to="/oral-cancer-screening" replace />} />
              <Route path="/services/osseous-surgery" element={<Navigate to="/osseous-surgery" replace />} />
              <Route path="/services/porcelain-veneers" element={<Navigate to="/porcelain-veneers" replace />} />
              <Route path="/services/preventative-dentistry" element={<Navigate to="/preventative-dentistry" replace />} />
              <Route path="/services/ridge-augmentation" element={<Navigate to="/ridge-augmentation" replace />} />
              <Route path="/services/ridge-preservation" element={<Navigate to="/ridge-preservation" replace />} />
              <Route path="/services/root-canals" element={<Navigate to="/root-canals" replace />} />
              <Route path="/services/scaling-root-planing" element={<Navigate to="/scaling-root-planing" replace />} />
              <Route path="/services/sedation" element={<Navigate to="/sedation" replace />} />
              <Route path="/services/teeth-whitening" element={<Navigate to="/teeth-whitening" replace />} />
              <Route path="/services/lanap" element={<Navigate to="/lanap" replace />} />
              <Route path="/specials/dental-implants-free-consult" element={<Navigate to="/dental-implants-free-consult-with-x-rays" replace />} />
              <Route path="/specials/invisalign-free-consultation" element={<Navigate to="/invisalign-free-consultation" replace />} />
              <Route path="/specials/179-healthy-cleaning" element={<Navigate to="/179-healthy-cleaning-exam-and-set-of-x-rays" replace />} />
              <Route path="/specials/99-emergency-exam" element={<Navigate to="/99-emergency-exam-and-x-ray-affordable-dental-services" replace />} />
              <Route path="/specials/free-lanap-consultation" element={<Navigate to="/free-lanap-consultation" replace />} />
              
              {/* Unsubscribe Routes */}
              <Route path="/unsubscribe/voting/:token" element={<VotingUnsubscribe />} />
              
              {/* 404 Catch-all */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Layout>
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;
